import { TRPCErrorBoundary } from '@client/components/_core/error-handling/TRPCErrorBoundary';
import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { Space } from '@client/components/_ui/layout/Space';
import { ReadOnlyBanner } from '@client/components/admin/ReadOnlyBanner';
import { useAdminModulePermissions } from '@client/hooks/admin/useAdminModulePermissions';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { usePathState } from '@client/hooks/navigation/usePathState';
import { createColumnHelper, PrismaTable, type PrismaTableColumn, usePrismaTable } from '@client/hooks/table-v2';
import type { RouterInputs, RouterOutputs } from '@client/trpc/client';
import { DEFAULT_PAGE_SIZE } from '@shared/constants';
import { formatDateTime } from '@shared/utils/core/formatting';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { useMemo } from 'react';
import { AppSettingFilters } from './AppSettingFilters';

export const Route = createFileRoute('/admin-console/advanced/app-settings/all')({
    staticData: {
        breadcrumb: {
            title: 'App Settings',
        },
        ui: {
            title: 'All app settings',
            subtitle: 'Manage application settings.',
        },
    },
    component: AppSettings,
});

type AppSetting = RouterOutputs['admin']['appSetting']['listAppSettings']['rows'][number];
type QueryVariables = RouterInputs['admin']['appSetting']['listAppSettings'];
type OrderBy = NonNullable<QueryVariables['orderBy']>;

function useAppSettingColumns(
    navigate: ReturnType<typeof useNavigate>,
    deleteAppSetting: any,
    refetch: () => void,
    isReadOnly: boolean,
): readonly PrismaTableColumn<AppSetting, OrderBy, any>[] {
    const columnHelper = createColumnHelper<AppSetting, OrderBy>();

    return useMemo(
        () => [
            columnHelper.accessor('name', {
                id: 'name',
                header: 'Name',
                sortable: 'name',
                className: 'whitespace-nowrap font-medium',
            }),
            columnHelper.accessor('value', {
                id: 'value',
                header: 'Value',
                cell: ({ value }) => <div className="max-w-60 truncate">{value}</div>,
                className: 'whitespace-nowrap',
            }),
            columnHelper.accessor('updatedAt', {
                id: 'updatedAt',
                header: 'Updated At',
                cell: ({ value }) => (value ? formatDateTime(new Date(value), 'yyyy-MM-dd HH:mm:ss') : '-'),
                className: 'whitespace-nowrap',
            }),
            columnHelper.actions({
                id: 'actions',
                cell: ({ row }) => (
                    <Space size="small">
                        <Button.Edit
                            onClick={() => {
                                navigate({
                                    to: '/admin-console/advanced/app-settings/all/edit/$name',
                                    params: { name: row.name },
                                });
                            }}
                        />
                        {!isReadOnly && (
                            <Button.Delete
                                confirmTitle="Delete App Setting"
                                confirmDescription="Are you sure you want to delete this setting?"
                                onConfirm={(onSuccess) =>
                                    deleteAppSetting(
                                        { name: row.name },
                                        {
                                            onSuccess: () => {
                                                void refetch();
                                                onSuccess();
                                            },
                                            onError: (error: Error) => {
                                                errorMessage.show(error.message || 'Failed to delete setting');
                                            },
                                        },
                                    )
                                }
                            />
                        )}
                    </Space>
                ),
                fixed: 'right',
            }),
        ],
        [navigate, deleteAppSetting, refetch, isReadOnly],
    );
}

function AppSettings() {
    const { trpc } = useTrpc();
    const navigate = useNavigate();
    const { isReadOnly } = useAdminModulePermissions('system');

    const [queryVariables, setQueryVariables] = usePathState<QueryVariables>('queryVariables', {
        limit: DEFAULT_PAGE_SIZE,
        orderBy: { name: 'asc' },
    });

    const {
        data: appSettings,
        isFetching,
        refetch,
        error,
    } = trpc.admin.appSetting.listAppSettings.useQuery(queryVariables);
    const { mutate: deleteAppSetting } = trpc.admin.appSetting.deleteAppSetting.useMutation();

    const columns = useAppSettingColumns(navigate, deleteAppSetting, refetch, isReadOnly);

    const tableProps = usePrismaTable({
        data: appSettings?.rows || [],
        total: appSettings?.total || 0,
        loading: isFetching,
        columns,
        rowKey: 'name',
        queryParams: queryVariables,
        onParamsChange: setQueryVariables,
        pagination: {
            position: 'both',
            showSizeChanger: true,
        },
        columnVisibility: {
            enabled: true,
            persistKey: 'app-settings-table-columns',
            enableReordering: true,
        },
        refreshable: {
            enabled: true,
            onRefresh: () => refetch(),
        },
    });

    return (
        <PageController route={Route}>
            <TRPCErrorBoundary error={error}>
                <ReadOnlyBanner module="system" />
                <TableToolbar
                    filters={
                        <AppSettingFilters
                            onChange={(where) => {
                                setQueryVariables((prev) => ({ ...prev, where, offset: 0 }));
                            }}
                        />
                    }
                    variant="enhanced"
                    actions={
                        !isReadOnly ? (
                            <Button
                                type="primary"
                                icon={<Plus />}
                                onClick={() => {
                                    navigate({ to: '/admin-console/advanced/app-settings/all/add' });
                                }}
                            >
                                Create Setting
                            </Button>
                        ) : undefined
                    }
                />

                <PrismaTable {...tableProps} size="small" scroll={{ x: 'max-content' }} />
                <Outlet />
            </TRPCErrorBoundary>
        </PageController>
    );
}
